import React, { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Shared rendering core for dot-globe patterns.
//
// A dot-globe pattern is a 3D rotating sphere of dots with great-circle
// connections, painted on top of a two-color diagonal background gradient.
// Variants differ only in:
//   - where the sphere is centered (cx, cy)
//   - how big the sphere is (radius)
//
// `createDotGlobePattern` returns a React component ready to be exported as
// the default of a pattern file. The placement is configured via the
// `placement` callback, which receives the canvas dimensions and the default
// auto-sized radius and returns the final {cx, cy, radius}.
//
// Lives in `internal/` so the auto-discovery in `patterns-js/index.js` (which
// is non-recursive) doesn't try to register it as a pattern.
// ---------------------------------------------------------------------------

const DEFAULT_BG_TOP = "#0b1220";
const DEFAULT_BG_BOTTOM = "#1e293b";
const DEFAULT_GLOBE = "#7dd3fc";

const DEFAULT_DOT_COUNT = 320;
const DOT_BASE_RADIUS = 1.3;
const DEFAULT_ROTATION_SPEED_Y = 0.0035; // radians per frame
const TILT = 0.45;
const DEFAULT_LINK_ANGLE = 0.32; // radians (~18 degrees)

function pickColor(colors, idx, fallback) {
  if (!Array.isArray(colors) || !colors.length) return fallback;
  return colors[idx] || colors[colors.length - 1] || fallback;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") return null;
  let value = hex.slice(1);
  if (value.length === 3) {
    value = value.split("").map((c) => c + c).join("");
  }
  if (value.length !== 6) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgba(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(255,255,255,${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// Fibonacci sphere distribution — even point coverage, no clumping.
function fibonacciSphere(count) {
  const points = new Array(count);
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i += 1) {
    const y = 1 - (i / Math.max(1, count - 1)) * 2;
    const radius = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = goldenAngle * i;
    points[i] = {
      x: Math.cos(theta) * radius,
      y,
      z: Math.sin(theta) * radius,
    };
  }
  return points;
}

// Pre-compute neighbour pairs whose angular distance is below LINK_ANGLE.
// Rotation doesn't change which dots are connected, so we only do this once.
function computeNeighbourPairs(points, maxAngle) {
  const cosLimit = Math.cos(maxAngle);
  const pairs = [];
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    for (let j = i + 1; j < points.length; j += 1) {
      const b = points[j];
      const dot = a.x * b.x + a.y * b.y + a.z * b.z;
      if (dot > cosLimit) {
        pairs.push(i, j);
      }
    }
  }
  return pairs;
}

/**
 * Build a React component rendering a dot-globe with a configurable placement.
 *
 * @param {object} opts
 * @param {(dims: {width:number, height:number, autoRadius:number}) =>
 *          {cx:number, cy:number, radius:number}} opts.placement
 *   Callback invoked every frame to decide where to draw the globe.
 *   `autoRadius` is the centered default (≈ min(w,h) * 0.36, clamped 80..320).
 * @param {number} [opts.dotCount]   Total candidate sphere points (before
 *   filtering). Default 320.
 * @param {number} [opts.linkAngle]  Max angular distance between connected
 *   dots, in radians. Default 0.32 (~18°). Use a smaller value when dotCount
 *   is increased to keep the wireframe density consistent.
 * @param {(p: {x:number, y:number, z:number}) => boolean} [opts.pointFilter]
 *   Optional predicate to drop sphere points before they're rendered.
 *   Useful for masking variants (e.g. only render dots that fall on land).
 *   The point coordinates are unit-sphere (x²+y²+z² = 1), with `y` as the
 *   pole axis.
 * @param {number} [opts.rotationSpeed]  Rotation speed around the vertical
 *   axis, in radians per frame. Default 0.0035 (~12s per revolution at 60fps).
 *   Negative values rotate the opposite direction.
 * @param {{x0:number, y0:number, x1:number, y1:number}} [opts.gradientDirection]
 *   Background gradient endpoints, normalized 0..1 of the canvas
 *   (color-1 lives at the start point, color-2 at the end point).
 *   Default is top-left → bottom-right `{0, 0, 1, 1}`.
 */
export function createDotGlobePattern({
  placement,
  dotCount = DEFAULT_DOT_COUNT,
  linkAngle = DEFAULT_LINK_ANGLE,
  pointFilter = null,
  rotationSpeed = DEFAULT_ROTATION_SPEED_Y,
  gradientDirection = { x0: 0, y0: 0, x1: 1, y1: 1 },
}) {
  return function DotGlobePattern({ colors = [], style }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const colorsRef = useRef({
      bgTop: DEFAULT_BG_TOP,
      bgBottom: DEFAULT_BG_BOTTOM,
      globe: DEFAULT_GLOBE,
    });

    useEffect(() => {
      colorsRef.current = {
        bgTop: pickColor(colors, 0, DEFAULT_BG_TOP),
        bgBottom: pickColor(colors, 1, DEFAULT_BG_BOTTOM),
        globe: pickColor(colors, 2, DEFAULT_GLOBE),
      };
    }, [colors]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return undefined;

      const ctx = canvas.getContext("2d");
      if (!ctx) return undefined;

      let width = 0;
      let height = 0;
      const dpr =
        typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

      const candidates = fibonacciSphere(dotCount);
      const points = pointFilter ? candidates.filter(pointFilter) : candidates;
      const pairs = computeNeighbourPairs(points, linkAngle);

      // [x, y, z, scale] per point
      const projected = new Float32Array(points.length * 4);

      let angleY = 0;

      const resize = () => {
        const parent = canvas.parentElement;
        if (!parent) return;
        // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
        // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
        width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
        height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
        canvas.width = Math.floor(width * dpr);
        canvas.height = Math.floor(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      const drawBackground = (bgTop, bgBottom) => {
        const { x0, y0, x1, y1 } = gradientDirection;
        const grad = ctx.createLinearGradient(
          x0 * width, y0 * height,
          x1 * width, y1 * height,
        );
        grad.addColorStop(0, bgTop);
        grad.addColorStop(1, bgBottom);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      };

      const tick = () => {
        const { bgTop, bgBottom, globe } = colorsRef.current;

        drawBackground(bgTop, bgBottom);

        const autoRadius = Math.max(80, Math.min(320, Math.min(width, height) * 0.36));
        const place = placement({ width, height, autoRadius });
        const cx = place.cx;
        const cy = place.cy;
        const radius = place.radius;

        // Camera distance — far enough to avoid fish-eye, close enough to
        // keep depth perceptible.
        const camera = radius * 3;

        const sinY = Math.sin(angleY);
        const cosY = Math.cos(angleY);
        const sinX = Math.sin(TILT);
        const cosX = Math.cos(TILT);

        // 1) project every point
        for (let i = 0; i < points.length; i += 1) {
          const p = points[i];

          const x1 = p.x * cosY + p.z * sinY;
          const z1 = -p.x * sinY + p.z * cosY;
          const y1 = p.y;

          const y2 = y1 * cosX - z1 * sinX;
          const z2 = y1 * sinX + z1 * cosX;
          const x2 = x1;

          const scale = camera / (camera - z2 * radius);
          const sx = cx + x2 * radius * scale;
          const sy = cy + y2 * radius * scale;

          const idx = i * 4;
          projected[idx] = sx;
          projected[idx + 1] = sy;
          projected[idx + 2] = z2;
          projected[idx + 3] = scale;
        }

        // 2) draw connections (back-to-front, depth-faded)
        ctx.lineWidth = 1;
        for (let k = 0; k < pairs.length; k += 2) {
          const i = pairs[k];
          const j = pairs[k + 1];
          const ai = i * 4;
          const bi = j * 4;
          const za = projected[ai + 2];
          const zb = projected[bi + 2];
          const zAvg = (za + zb) * 0.5;
          if (zAvg < -0.15) continue;
          const depth = (zAvg + 1) * 0.5;
          const alpha = 0.08 + depth * 0.45;
          ctx.strokeStyle = rgba(globe, alpha);
          ctx.beginPath();
          ctx.moveTo(projected[ai], projected[ai + 1]);
          ctx.lineTo(projected[bi], projected[bi + 1]);
          ctx.stroke();
        }

        // 3) draw dots back-to-front
        const order = new Array(points.length);
        for (let i = 0; i < points.length; i += 1) order[i] = i;
        order.sort((a, b) => projected[a * 4 + 2] - projected[b * 4 + 2]);

        for (let k = 0; k < order.length; k += 1) {
          const i = order[k];
          const idx = i * 4;
          const z = projected[idx + 2];
          const scale = projected[idx + 3];
          const depth = (z + 1) * 0.5;
          const alpha = 0.25 + depth * 0.75;
          const r = DOT_BASE_RADIUS * (0.55 + depth * 0.75) * scale;
          ctx.fillStyle = rgba(globe, alpha);
          ctx.beginPath();
          ctx.arc(projected[idx], projected[idx + 1], r, 0, Math.PI * 2);
          ctx.fill();
        }

        angleY += rotationSpeed;
        rafRef.current = requestAnimationFrame(tick);
      };

      resize();
      rafRef.current = requestAnimationFrame(tick);

      let ro = null;
      if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
        ro = new ResizeObserver(resize);
        ro.observe(canvas.parentElement);
      } else if (typeof window !== "undefined") {
        window.addEventListener("resize", resize);
      }

      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (ro) {
          try { ro.disconnect(); } catch { /* noop */ }
        } else if (typeof window !== "undefined") {
          window.removeEventListener("resize", resize);
        }
      };
    }, []);

    return (
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          pointerEvents: "none",
          ...style,
        }}
      />
    );
  };
}
